import * as Sentry from '@sentry/nextjs'

import { isDebugModeEnabled } from '@/lib/observability/logger/utils/debug-mode'

/**
 * Hash a wallet address using SHA-256 for GDPR compliance.
 * This allows grouping errors by user without exposing the actual wallet address.
 *
 * @param address - The wallet address to hash
 * @returns Promise resolving to the hashed address (hex string)
 */
export async function hashWalletAddress(address: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(address)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Safely add a breadcrumb to Sentry without breaking the application if it fails.
 */
export function safeSentryBreadcrumb(breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0]): void {
  try {
    Sentry.addBreadcrumb(breadcrumb)
    // The console mirror is debug-only because breadcrumbs are otherwise invisible outside Sentry.
    if (isDebugModeEnabled())
      console.debug('[breadcrumb]', breadcrumb.category, breadcrumb.message, breadcrumb.data)
  } catch {
    // Silent fail: monitoring should never break the app
  }
}
